import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Box, Typography, IconButton, Button, CircularProgress, Tooltip, TextField,
} from '@mui/material';
import {
  PlayArrow, Pause, Edit, DragIndicator, RemoveCircleOutline, MusicNote,
  Speaker, SpeakerOutlined, AddToQueue, ImportExport, PlaylistAdd, LibraryAdd,
  DeleteOutline,
} from '@mui/icons-material';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { useOrientation } from '../../hooks/useOrientation';
import { useManualTouchScroll, TOUCH_SCROLL_PANEL_SX } from '../../hooks/useManualTouchScroll';
import {
  SpotifyPlaylist,
  SpotifyPlaylistTrackRow,
  SonosSpeaker,
  getSpotifyPlaylistTracks,
  extractSpotifyPlaylistTrackItems,
  parseSpotifyPlaylistTrackRow,
  rememberPlaylistTrackIndex,
  setQueuedPlaylistContext,
  reorderSpotifyPlaylistTracks,
  removeTracksFromSpotifyPlaylist,
  updateSpotifyPlaylist,
  queueAdd,
  convertSpotifyPlaylist,
  deleteSpotifyPlaylist,
  isSonosPlaying,
} from '../../services/sonosApi';
import { SONOS_BLUE, SURFACE, SURFACE_BORDER, sonosIconTapSx } from './sonosUiStyles';
import SonosSideSlidePanel from './SonosSideSlidePanel';
import SonosPressFlashIconButton from './SonosPressFlashIconButton';
import SonosConfirmDialog from './SonosConfirmDialog';
import AddToPlaylistDialog from './AddToPlaylistDialog';
import { TrackArt, TrackPlayButton, TRACK_ROW_ACTIONS_SX, TRACK_ACTION_ICON_SX } from './sonosTrackUi';
import SoundBars from './SoundBars';
import { SonosMessageHandler } from './sonosFeedback';
import { spotifyErrorMessage } from './spotifyRateLimit';
import { formatTotalDuration } from './sonosDuration';

export type PlaylistTrackRow = SpotifyPlaylistTrackRow;

function normTrackText(value?: string | null): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Spotify suele traer "A, B"; Sonos a veces solo "A". */
function artistsLooselyMatch(a?: string | null, b?: string | null): boolean {
  const na = normTrackText(a);
  const nb = normTrackText(b);
  if (!na || !nb) return true;
  if (na === nb || na.includes(nb) || nb.includes(na)) return true;
  const firstA = na.split(',')[0]?.trim();
  const firstB = nb.split(',')[0]?.trim();
  return Boolean(
    firstA && firstB && (firstA === firstB || firstA.includes(firstB) || firstB.includes(firstA)),
  );
}

function titlesLooselyMatch(a?: string | null, b?: string | null): boolean {
  const na = normTrackText(a);
  const nb = normTrackText(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const strip = (s: string) => s.replace(/\s*[([].*$/, '').trim();
  const sa = strip(na);
  const sb = strip(nb);
  return Boolean(sa && sb && (sa === sb || sa.includes(sb) || sb.includes(sa)));
}

function isPlaylistTrackNowPlaying(
  track: PlaylistTrackRow,
  coordinator: SonosSpeaker | null | undefined,
): boolean {
  if (!coordinator || !isSonosPlaying(coordinator.playback_state)) return false;
  if (!titlesLooselyMatch(track.name, coordinator.current_title)) return false;
  if (!artistsLooselyMatch(track.artists, coordinator.current_artist)) return false;
  return true;
}

const PLAYLIST_TRACK_INDICATOR_SX = {
  width: 36,
  height: 36,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

interface PlaylistDetailSheetProps {
  open: boolean;
  playlist: SpotifyPlaylist | null;
  onClose: () => void;
  onPlayPlaylist: (pl: SpotifyPlaylist) => void;
  onPlayTrack: (
    trackUri: string,
    pl: SpotifyPlaylist,
    trackIndex: number,
    track: PlaylistTrackRow,
  ) => void;
  onAddToQueue?: (pl: SpotifyPlaylist, tracks?: PlaylistTrackRow[]) => void;
  onReplaceQueue?: (pl: SpotifyPlaylist, tracks?: PlaylistTrackRow[]) => void;
  /**
   * Cola local («este dispositivo»): si está definido, «Agregar a cola» por tema
   * usa esto en lugar de queueAdd(speakerId) (el id fake -1 no es un parlante).
   */
  onQueueTrackUri?: (track: PlaylistTrackRow) => Promise<void> | void;
  onPause?: () => void;
  isPlaying?: boolean;
  coordinator?: SonosSpeaker | null;
  destinationSpeakers?: SonosSpeaker[];
  onOpenDestination?: () => void;
  canPlay?: boolean;
  canControl?: boolean;
  onMessage?: SonosMessageHandler;
  onConverted?: () => void;
  onDeleted?: () => void;
  onRenamed?: (pl: SpotifyPlaylist) => void;
}

const ViewTrackRow: React.FC<{
  track: PlaylistTrackRow;
  canPlay: boolean;
  canControl: boolean;
  isNowPlaying: boolean;
  onPlay: () => void;
  onAddToQueue: () => void;
  onAddToPlaylist: () => void;
}> = ({ track, canPlay, canControl, isNowPlaying, onPlay, onAddToQueue, onAddToPlaylist }) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.85,
        borderBottom: `1px solid ${SURFACE_BORDER}`,
        touchAction: 'pan-y',
      }}
    >
      <TrackArt url={track.imageUrl} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {track.name}
        </Typography>
        <Typography sx={{
          color: '#999',
          fontSize: 11,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {track.artists}
        </Typography>
      </Box>
      <Box sx={TRACK_ROW_ACTIONS_SX}>
        <Tooltip title={t('musicSystem.search.addToQueue')}>
          <span>
            <IconButton size="small" onClick={onAddToQueue} disabled={!canControl} sx={{ color: '#aaa', ...TRACK_ACTION_ICON_SX }}>
              <PlaylistAdd sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('musicSystem.search.addToPlaylist')}>
          <span>
            <IconButton size="small" onClick={onAddToPlaylist} disabled={!canControl} sx={{ color: '#aaa', ...TRACK_ACTION_ICON_SX }}>
              <LibraryAdd sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={PLAYLIST_TRACK_INDICATOR_SX}>
          {isNowPlaying ? (
            <SoundBars active height={16} sx={{ mr: 0, width: 22 }} />
          ) : (
            <TrackPlayButton disabled={!canPlay} onClick={onPlay} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const EditTrackRow: React.FC<{
  id: string;
  track: PlaylistTrackRow;
  onRemove: () => void;
}> = ({ id, track, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.75,
        borderBottom: `1px solid ${SURFACE_BORDER}`,
      }}
    >
      <IconButton
        size="small"
        onClick={onRemove}
        sx={{ color: '#e53935', p: 0.25, flexShrink: 0 }}
      >
        <RemoveCircleOutline sx={{ fontSize: 22 }} />
      </IconButton>
      <TrackArt url={track.imageUrl} size={40} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {track.name}
        </Typography>
        <Typography sx={{ color: '#999', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {track.artists}
        </Typography>
      </Box>
      <IconButton
        size="small"
        sx={{ color: '#888', p: 0.25, flexShrink: 0, cursor: 'grab', touchAction: 'none' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicator sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

const PlaylistDetailSheet: React.FC<PlaylistDetailSheetProps> = ({
  open,
  playlist,
  onClose,
  onPlayPlaylist,
  onPlayTrack,
  onAddToQueue,
  onReplaceQueue,
  onQueueTrackUri,
  onPause,
  isPlaying = false,
  coordinator = null,
  destinationSpeakers = [],
  onOpenDestination,
  canPlay = true,
  canControl = true,
  onMessage,
  onConverted,
  onDeleted,
  onRenamed,
}) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const [tracks, setTracks] = useState<PlaylistTrackRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [canConvert, setCanConvert] = useState(false);
  const [converting, setConverting] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState(playlist?.name ?? '');
  const [nameEditing, setNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [instantHide, setInstantHide] = useState(false);
  const [removePrompt, setRemovePrompt] = useState<{ index: number; name: string } | null>(null);
  const [replaceQueuePrompt, setReplaceQueuePrompt] = useState(false);
  const [addToPlaylistUri, setAddToPlaylistUri] = useState<string | null>(null);
  const [addToPlaylistOpen, setAddToPlaylistOpen] = useState(false);
  /** URI del tema tocado: Sonos tarda en publicar current_title tras el play. */
  const [optimisticPlayingUri, setOptimisticPlayingUri] = useState<string | null>(null);
  const trackScrollRef = useManualTouchScroll(open);

  const speakerId = coordinator?.id ?? null;
  const isLocalDestination = Boolean(onQueueTrackUri) || (speakerId != null && speakerId <= 0);

  useEffect(() => {
    if (!open) {
      setOptimisticPlayingUri(null);
      return;
    }
    if (!isPlaying) {
      setOptimisticPlayingUri(null);
      return;
    }
    if (!optimisticPlayingUri) return;
    const optimisticTrack = tracks.find((tr) => tr.uri === optimisticPlayingUri);
    if (!optimisticTrack) {
      setOptimisticPlayingUri(null);
      return;
    }
    if (isPlaylistTrackNowPlaying(optimisticTrack, coordinator)) {
      setOptimisticPlayingUri(null);
      return;
    }
    const otherPlaying = tracks.some(
      (tr) => tr.uri !== optimisticPlayingUri && isPlaylistTrackNowPlaying(tr, coordinator),
    );
    if (otherPlaying) setOptimisticPlayingUri(null);
  }, [open, isPlaying, optimisticPlayingUri, tracks, coordinator]);

  const addTrackToQueue = useCallback(async (track: PlaylistTrackRow) => {
    if (!canControl) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    if (onQueueTrackUri || isLocalDestination) {
      if (!onQueueTrackUri) {
        onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
        return;
      }
      try {
        await onQueueTrackUri(track);
        onMessage?.(t('musicSystem.search.addedToQueue'), 'success');
      } catch (e: any) {
        onMessage?.(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
      }
      return;
    }
    if (!speakerId || speakerId <= 0) {
      onMessage?.(t('musicSystem.playback.selectSpeaker'), 'error');
      return;
    }
    try {
      await queueAdd(speakerId, { uri: track.uri });
      onMessage?.(t('musicSystem.search.addedToQueue'), 'success');
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.playback.playError'), 'error');
    }
  }, [speakerId, canControl, onMessage, t, onQueueTrackUri, isLocalDestination]);

  const openAddToPlaylist = useCallback((uri: string) => {
    setAddToPlaylistUri(uri);
    setAddToPlaylistOpen(true);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 10 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 280, tolerance: 8 } }),
  );

  const loadTracks = useCallback(async (pl: SpotifyPlaylist) => {
    setLoading(true);
    setLoadError(null);
    setCanConvert(false);
    const declaredTotal = typeof pl.tracks === 'number' ? pl.tracks : 0;
    try {
      const data = await getSpotifyPlaylistTracks(pl.id);
      const rawItems = extractSpotifyPlaylistTrackItems(data);
      const parsed = rawItems.map(parseSpotifyPlaylistTrackRow).filter(Boolean) as PlaylistTrackRow[];
      rememberPlaylistTrackIndex(pl.id, parsed);
      setQueuedPlaylistContext(pl.id);
      setTracks(parsed);
      if (parsed.length === 0 && rawItems.length > 0) {
        setLoadError(t('musicSystem.playlists.detail.unavailableTracks'));
      } else if (parsed.length === 0 && declaredTotal > 0) {
        // Caché vacía con meta N temas (playlist ajena / 403): ofrecer convertir.
        setLoadError(t('musicSystem.playlists.detail.foreignTracksHint'));
        setCanConvert(true);
      }
    } catch (e: any) {
      setTracks([]);
      const detail = e?.response?.data?.detail;
      setLoadError(typeof detail === 'string' ? detail : t('musicSystem.playlists.detail.loadTracksError'));
      if (e?.response?.status === 403 || declaredTotal > 0) setCanConvert(true);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleConvert = useCallback(async () => {
    if (!playlist) return;
    setConverting(true);
    try {
      const res = await convertSpotifyPlaylist(playlist.id, { uri: playlist.uri, name: playlist.name });
      onMessage?.(t('musicSystem.playlists.detail.convertedOk', { count: res.count ?? 0 }), 'success');
      onConverted?.();
      onClose();
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.search.libraryError'), 'error');
    } finally {
      setConverting(false);
    }
  }, [playlist, onMessage, onConverted, onClose, t]);

  const handleDeletePlaylist = useCallback(async () => {
    if (!playlist) return;
    setDeleting(true);
    try {
      await deleteSpotifyPlaylist(playlist.id);
      setDeletePrompt(false);
      onMessage?.(t('musicSystem.playlists.detail.deletedOk'), 'success');
      onDeleted?.();
      onClose();
    } catch (e: any) {
      onMessage?.(e?.response?.data?.detail || t('musicSystem.playlists.detail.deleteError'), 'error');
    } finally {
      setDeleting(false);
    }
  }, [playlist, onMessage, onDeleted, onClose, t]);

  useEffect(() => {
    if (!open || !playlist) {
      setEditMode(false);
      setNameEditing(false);
      setTracks([]);
      setLoadError(null);
      setInstantHide(false);
      setRemovePrompt(null);
      setReplaceQueuePrompt(false);
      setAddToPlaylistOpen(false);
      setAddToPlaylistUri(null);
      setDeletePrompt(false);
      setOptimisticPlayingUri(null);
      return;
    }
    setInstantHide(false);
    setEditMode(false);
    setNameEditing(false);
    void loadTracks(playlist);
  }, [open, playlist, loadTracks]);

  useEffect(() => {
    if (!playlist) return;
    setDisplayName(playlist.name);
  }, [playlist]);

  const runPlayAction = useCallback((action: () => void) => {
    flushSync(() => setInstantHide(true));
    onClose();
    requestAnimationFrame(() => {
      requestAnimationFrame(action);
    });
  }, [onClose]);

  const trackCount = tracks.length > 0
    ? tracks.length
    : (playlist?.tracks != null && playlist.tracks > 0 ? playlist.tracks : tracks.length);
  const hasPlaylistContent = tracks.length > 0 || (playlist?.tracks != null && playlist.tracks > 0);
  const totalDurationMs = useMemo(
    () => tracks.reduce((sum, tr) => sum + (tr.durationMs || 0), 0),
    [tracks],
  );
  const durationLabel = formatTotalDuration(totalDurationMs);

  const metaLabel = useMemo(() => {
    if (trackCount == null || trackCount <= 0) return null;
    return `${trackCount} ${t('musicSystem.playback.tracks')}`;
  }, [trackCount, t]);

  const errorMessage = useCallback(
    (e: any, fallbackKey: string): string => spotifyErrorMessage(e, t, fallbackKey),
    [t],
  );

  const commitPlaylistRename = useCallback(async () => {
    if (!playlist) return;
    const next = nameDraft.trim();
    setNameEditing(false);
    if (!next || next === displayName) {
      setNameDraft(displayName);
      return;
    }
    const previous = displayName;
    setDisplayName(next);
    setBusy(true);
    try {
      await updateSpotifyPlaylist(playlist.id, { name: next });
      onRenamed?.({ ...playlist, name: next });
    } catch (e: any) {
      setDisplayName(previous);
      setNameDraft(previous);
      onMessage?.(errorMessage(e, 'musicSystem.playlists.detail.renameError'), 'error');
    } finally {
      setBusy(false);
    }
  }, [playlist, nameDraft, displayName, onRenamed, onMessage, errorMessage]);

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!playlist) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = tracks.findIndex((_, i) => `pt-${i}` === active.id);
    const newIndex = tracks.findIndex((_, i) => `pt-${i}` === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(tracks, oldIndex, newIndex);
    setTracks(next);
    setBusy(true);
    try {
      await reorderSpotifyPlaylistTracks(playlist.id, oldIndex, newIndex);
    } catch (e: any) {
      await loadTracks(playlist);
      onMessage?.(errorMessage(e, 'musicSystem.playlists.detail.reorderError'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveTrack = async (index: number) => {
    if (!playlist) return;
    const uri = tracks[index]?.uri;
    if (!uri) return;
    setBusy(true);
    try {
      await removeTracksFromSpotifyPlaylist(playlist.id, [uri]);
      setTracks((prev) => prev.filter((_, i) => i !== index));
      onMessage?.(t('musicSystem.playlists.detail.trackRemoved'), 'success');
    } catch (e: any) {
      onMessage?.(errorMessage(e, 'musicSystem.playlists.detail.removeTrackError'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const requestRemoveTrack = (index: number) => {
    const track = tracks[index];
    if (!track) return;
    setRemovePrompt({
      index,
      name: track.name?.trim() || `Track ${index + 1}`,
    });
  };

  const confirmRemoveTrack = () => {
    if (!removePrompt) return;
    const { index } = removePrompt;
    setRemovePrompt(null);
    void handleRemoveTrack(index);
  };

  const confirmReplaceQueue = () => {
    if (!playlist) return;
    setReplaceQueuePrompt(false);
    // Pasar los tracks ya cargados en el modal para no re-fetchear (caché incompleta).
    const snapshot = tracks.slice();
    runPlayAction(() => onReplaceQueue?.(playlist, snapshot));
  };

  if (!playlist || instantHide) return null;

  const hasSpeaker = Boolean(coordinator);
  const destCount = hasSpeaker ? destinationSpeakers.length : 0;
  const deviceLabel = !hasSpeaker
    ? t('musicSystem.playback.selectSpeaker')
    : destCount === 0
      ? '—'
      : destinationSpeakers.map((s) => s.nombre).join(' + ');

  const handleMainPlayPause = () => {
    if (isPlaying) {
      onPause?.();
      return;
    }
    runPlayAction(() => onPlayPlaylist(playlist));
  };

  const actionsDisabled = editMode;
  const actionIconSize = isHorizontal ? 18 : 22;
  const actionBtnPad = isHorizontal ? 0.15 : 0.5;
  const playBtnSize = isHorizontal ? 38 : 52;
  const playIconSize = isHorizontal ? 22 : 30;
  const playArrowSize = isHorizontal ? 24 : 32;
  const compactActionBtnSx = isHorizontal
    ? { minWidth: 28, minHeight: 28, width: 28, height: 28 }
    : {};
  const mutedActionSx = {
    color: '#666',
    opacity: 0.45,
    p: actionBtnPad,
    ...compactActionBtnSx,
    ...sonosIconTapSx,
  } as const;

  const toolbar = (
    <Box sx={{
      flexShrink: 0,
      px: isHorizontal ? 1.25 : 1.5,
      py: isHorizontal ? 1 : 1.25,
      borderBottom: `1px solid ${SURFACE_BORDER}`,
      background: playlist.image_url
        ? `linear-gradient(180deg, rgba(80,30,30,0.85) 0%, #000 70%), url(${playlist.image_url}) center top / cover no-repeat`
        : 'linear-gradient(180deg, #2a2a2a 0%, #000 70%)',
    }}>
      <Box sx={{
        position: 'relative',
        display: 'flex',
        gap: isHorizontal ? 1 : 1.25,
        alignItems: 'center',
        minHeight: isHorizontal ? 96 : 104,
      }}>
        <Box sx={{
          width: isHorizontal ? 88 : 96,
          height: isHorizontal ? 88 : 96,
          borderRadius: 1.5,
          flexShrink: 0,
          boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
          overflow: 'hidden',
          bgcolor: SURFACE, border: `1px solid ${SURFACE_BORDER}`,
        }}>
          {playlist.image_url ? (
            <Box component="img" src={playlist.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MusicNote sx={{ color: '#555', fontSize: isHorizontal ? 32 : 36 }} />
            </Box>
          )}
        </Box>

        <Box sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 0.5,
          /* En horizontal, reservar espacio para el cluster compacto de acciones. */
          pr: isHorizontal ? '9.5rem' : 0,
        }}>
          {editMode && nameEditing ? (
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={() => { void commitPlaylistRename(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setNameDraft(displayName);
                  setNameEditing(false);
                }
              }}
              disabled={busy}
              inputProps={{ 'aria-label': t('musicSystem.playlists.detail.editTitle') }}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  fontSize: 18,
                  fontWeight: 800,
                  bgcolor: 'rgba(0,0,0,0.35)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.55)' },
                  '&.Mui-focused fieldset': { borderColor: '#1ed760' },
                },
                '& .MuiOutlinedInput-input': { py: 0.75, px: 1 },
              }}
            />
          ) : (
            <Typography
              onClick={editMode ? () => {
                setNameDraft(displayName);
                setNameEditing(true);
              } : undefined}
              sx={{
                color: '#fff',
                fontSize: 18,
                fontWeight: 800,
                lineHeight: 1.15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                cursor: editMode ? 'text' : 'default',
                borderRadius: editMode ? 1 : 0,
                outline: editMode ? '1px dashed rgba(255,255,255,0.28)' : 'none',
                outlineOffset: 2,
                px: editMode ? 0.5 : 0,
                py: editMode ? 0.25 : 0,
              }}
            >
              {displayName}
            </Typography>
          )}
          {metaLabel && (
            <Typography sx={{
              color: 'rgba(255,255,255,0.78)',
              fontSize: isHorizontal ? 12.5 : 14,
              fontWeight: 600,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {metaLabel}
            </Typography>
          )}
          {durationLabel && trackCount != null && trackCount > 0 && (
            <Typography sx={{
              color: 'rgba(255,255,255,0.62)',
              fontSize: isHorizontal ? 12 : 13,
              fontWeight: 600,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {durationLabel}
            </Typography>
          )}
          <Box
            onClick={actionsDisabled ? undefined : onOpenDestination}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
              cursor: !actionsDisabled && onOpenDestination ? 'pointer' : 'default',
              opacity: actionsDisabled ? 0.45 : 1,
            }}
          >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              {hasSpeaker ? (
                <Speaker sx={{ color: '#fff', fontSize: 22 }} />
              ) : (
                <SpeakerOutlined sx={{ color: SONOS_BLUE, fontSize: 24 }} />
              )}
              {hasSpeaker && destCount > 0 && (
                <Box sx={{
                  position: 'absolute',
                  top: -4,
                  right: -8,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  backgroundColor: SONOS_BLUE,
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {destCount}
                </Box>
              )}
            </Box>
            <Typography sx={{
              color: '#fff',
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {deviceLabel}
            </Typography>
          </Box>
        </Box>

        <Box sx={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: isHorizontal ? 0.15 : 0.85,
          flexShrink: 0,
          zIndex: 2,
          pointerEvents: 'auto',
        }}>
          <Tooltip title={t('musicSystem.playlists.detail.deleteTitle')}>
            <span>
              <IconButton
                onClick={() => setDeletePrompt(true)}
                disabled={actionsDisabled}
                disableRipple
                sx={actionsDisabled
                  ? mutedActionSx
                  : { color: '#e53935', p: actionBtnPad, ...compactActionBtnSx, ...sonosIconTapSx }}
              >
                <DeleteOutline sx={{ fontSize: actionIconSize }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('musicSystem.playlists.detail.editTitle')}>
            <span>
              <IconButton
                onClick={() => {
                  if (editMode) {
                    setNameEditing(false);
                    setNameDraft(displayName);
                    setEditMode(false);
                  } else {
                    setEditMode(true);
                  }
                }}
                disableRipple
                sx={{
                  color: editMode ? '#1ed760' : '#fff',
                  p: actionBtnPad,
                  ...compactActionBtnSx,
                  ...sonosIconTapSx,
                }}
              >
                <Edit sx={{ fontSize: actionIconSize }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('musicSystem.playlists.detail.addToQueueEnd')}>
            <span>
              <SonosPressFlashIconButton
                disabled={actionsDisabled || !canPlay || !hasPlaylistContent}
                onClick={() => onAddToQueue?.(playlist, tracks)}
                sx={actionsDisabled
                  ? mutedActionSx
                  : { color: '#fff', p: actionBtnPad, ...compactActionBtnSx }}
              >
                <AddToQueue sx={{ fontSize: actionIconSize }} />
              </SonosPressFlashIconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('musicSystem.playlists.detail.replaceQueue')}>
            <span>
              <SonosPressFlashIconButton
                disabled={actionsDisabled || !canPlay || !hasPlaylistContent}
                onClick={() => setReplaceQueuePrompt(true)}
                sx={actionsDisabled
                  ? mutedActionSx
                  : { color: '#fff', p: actionBtnPad, ...compactActionBtnSx }}
              >
                <ImportExport sx={{ fontSize: actionIconSize }} />
              </SonosPressFlashIconButton>
            </span>
          </Tooltip>
          <Tooltip title={isPlaying
            ? t('musicSystem.playback.pause')
            : t('musicSystem.playlists.detail.playPlaylist')}>
            <span>
              <IconButton
                disabled={actionsDisabled || !canPlay || !hasPlaylistContent}
                onClick={handleMainPlayPause}
                disableRipple
                sx={{
                  width: playBtnSize,
                  height: playBtnSize,
                  flexShrink: 0,
                  ml: isHorizontal ? 0.05 : 0.15,
                  bgcolor: actionsDisabled ? '#333' : '#1ed760',
                  color: actionsDisabled ? '#666' : '#000',
                  opacity: actionsDisabled ? 0.45 : 1,
                  transition: 'background-color 0.1s ease, filter 0.1s ease',
                  WebkitTapHighlightColor: 'transparent',
                  '@media (hover: hover)': {
                    '&:hover': { bgcolor: actionsDisabled ? '#333' : '#1bc955' },
                  },
                  '&:active': {
                    bgcolor: actionsDisabled ? '#333' : '#18b34f',
                    filter: actionsDisabled ? 'none' : 'brightness(1.06)',
                  },
                  '&.Mui-disabled': { bgcolor: '#333', color: '#666', filter: 'none', opacity: 0.45 },
                }}
              >
                {isPlaying ? (
                  <Pause sx={{ fontSize: playIconSize }} />
                ) : (
                  <PlayArrow sx={{ fontSize: playArrowSize }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  const trackList = (
    <Box
      ref={trackScrollRef}
      data-audio-inner-scroll=""
      sx={{
        ...TOUCH_SCROLL_PANEL_SX,
        px: editMode ? 1 : 1.5,
        py: 0.75,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: SONOS_BLUE }} size={28} />
        </Box>
      ) : tracks.length === 0 ? (
        <Box sx={{ py: 2, px: 1 }}>
          <Typography sx={{
            color: loadError ? '#ffb74d' : '#999',
            fontSize: 12,
            textAlign: 'center',
            lineHeight: 1.45,
          }}>
            {loadError || t('musicSystem.playlists.detail.emptyTracks')}
          </Typography>
          {canConvert && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                onClick={handleConvert}
                disabled={converting}
                startIcon={converting
                  ? <CircularProgress size={16} sx={{ color: '#000' }} />
                  : <LibraryAdd sx={{ fontSize: 20 }} />}
                sx={{
                  bgcolor: '#1ed760',
                  color: '#000',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2,
                  py: 0.85,
                  borderRadius: 2,
                  '@media (hover: hover)': { '&:hover': { bgcolor: '#1bc955' } },
                  '&.Mui-disabled': { bgcolor: '#333', color: '#666' },
                }}
              >
                {converting
                  ? t('musicSystem.playlists.detail.converting')
                  : t('musicSystem.playlists.detail.convertToMine')}
              </Button>
            </Box>
          )}
        </Box>
      ) : editMode ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tracks.map((_, i) => `pt-${i}`)} strategy={verticalListSortingStrategy}>
            {tracks.map((track, i) => (
              <EditTrackRow
                key={`${track.uri}-${i}`}
                id={`pt-${i}`}
                track={track}
                onRemove={() => requestRemoveTrack(i)}
              />
            ))}
          </SortableContext>
        </DndContext>
      ) : (
        tracks.map((track, i) => (
          <ViewTrackRow
            key={`${track.uri}-${i}`}
            track={track}
            canPlay={canPlay}
            canControl={canControl}
            isNowPlaying={
              optimisticPlayingUri
                ? optimisticPlayingUri === track.uri
                : isPlaylistTrackNowPlaying(track, coordinator)
            }
            onPlay={() => {
              setOptimisticPlayingUri(track.uri);
              onPlayTrack(track.uri, playlist, i, track);
            }}
            onAddToQueue={() => { void addTrackToQueue(track); }}
            onAddToPlaylist={() => openAddToPlaylist(track.uri)}
          />
        ))
      )}
      {busy && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} sx={{ color: SONOS_BLUE }} />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <SonosSideSlidePanel
        open={open}
        onClose={onClose}
        testId="playlist-detail-modal"
        title={editMode ? t('musicSystem.playlists.detail.editTitle') : displayName}
        toolbar={toolbar}
      >
        {trackList}
      </SonosSideSlidePanel>
      <SonosConfirmDialog
        open={Boolean(removePrompt)}
        historyKey="sonos-playlist-remove-track"
        title={t('musicSystem.playlists.detail.removeTrackConfirmTitle')}
        message={t('musicSystem.playlists.detail.removeTrackConfirmBody', { name: removePrompt?.name ?? '' })}
        onCancel={() => setRemovePrompt(null)}
        onConfirm={confirmRemoveTrack}
      />
      <SonosConfirmDialog
        open={replaceQueuePrompt}
        historyKey="sonos-playlist-replace-queue"
        title={t('musicSystem.playlists.detail.replaceQueueConfirmTitle')}
        message={t('musicSystem.playlists.detail.replaceQueueConfirmBody', { name: displayName })}
        confirmLabel={t('musicSystem.playlists.detail.replaceQueueConfirmAction')}
        onCancel={() => setReplaceQueuePrompt(false)}
        onConfirm={confirmReplaceQueue}
      />
      <AddToPlaylistDialog
        open={addToPlaylistOpen}
        trackUri={addToPlaylistUri}
        onClose={() => { setAddToPlaylistOpen(false); setAddToPlaylistUri(null); }}
        onAdded={() => onMessage?.(t('musicSystem.search.addedToPlaylist'), 'success')}
        onError={(msg) => onMessage?.(msg, 'error')}
      />
      <SonosConfirmDialog
        open={deletePrompt}
        historyKey="sonos-playlist-delete"
        title={t('musicSystem.playlists.detail.deleteTitle')}
        message={t('musicSystem.playlists.detail.deleteConfirmBody', { name: displayName })}
        confirmLabel={t('musicSystem.playlists.detail.deleteConfirmAction')}
        onCancel={() => { if (!deleting) setDeletePrompt(false); }}
        onConfirm={() => { void handleDeletePlaylist(); }}
      />
    </>
  );
};

export default PlaylistDetailSheet;
